import { memo, useState, useCallback } from 'react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Separator,
  Checkbox,
} from '@flote/ui';
import { Loader2, User, Phone, MessageSquareOff, AlertCircle } from 'lucide-react';
import type { CreateRecipientRequest, UpdateRecipientRequest, Recipient } from '@flote/contracts/http';

export type RecipientFormData = CreateRecipientRequest;

interface RecipientFormProps {
  onSubmit: (data: CreateRecipientRequest | UpdateRecipientRequest) => Promise<void>;
  isProcessing?: boolean;
  initialData?: Recipient;
  mode?: 'create' | 'edit';
}

export const RecipientForm = memo<RecipientFormProps>(({
  onSubmit,
  isProcessing = false,
  initialData,
  mode = 'create',
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState(initialData?.name || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [notes, setNotes] = useState(initialData?.notes || '');
  const [skipSMSNotifications, setSkipSMSNotifications] = useState(
    initialData?.skipSMSNotifications || false
  );
  const [skipPhoneNumberValidation, setSkipPhoneNumberValidation] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  const formatPhoneNumber = (value: string): string => {
    // If it starts with +, keep it as international format
    if (value.startsWith('+')) {
      return value.replace(/[^\d+]/g, '');
    }
    // Otherwise format as US phone number
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    // Clear phone error when user types
    if (errors.phone) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.phone;
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Recipient name is required';
    }

    if (mode === 'create') {
      if (!phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else {
        // Basic phone validation
        const digits = phone.replace(/\D/g, '');
        if (!phone.startsWith('+') && digits.length < 10) {
          newErrors.phone = 'Please enter a valid phone number';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (mode === 'create') {
        // Clean phone for API - remove formatting but keep + for international
        let cleanPhone = phone;
        if (!phone.startsWith('+')) {
          cleanPhone = phone.replace(/\D/g, '');
        }

        const createRequest: CreateRecipientRequest = {
          name: name.trim(),
          phone: cleanPhone,
          notes: notes.trim() || undefined,
          skipSMSNotifications,
          skipPhoneNumberValidation: skipPhoneNumberValidation || undefined,
        };
        await onSubmit(createRequest);
      } else {
        // Update mode - phone cannot be changed
        const updateRequest: UpdateRecipientRequest = {
          name: name.trim(),
          notes: notes.trim() || undefined,
          skipSMSNotifications,
        };
        await onSubmit(updateRequest);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [name, phone, notes, skipSMSNotifications, skipPhoneNumberValidation, mode, onSubmit]);

  const processing = isSubmitting || isProcessing;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Recipient Information Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4 text-muted-foreground" />
          Recipient Information
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            placeholder="John Doe"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing}
            className={errors.name ? 'border-destructive' : ''}
          />
          {errors.name && (
            <p className="text-xs text-destructive">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">
            Phone Number {mode === 'create' ? '*' : ''}
          </Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567 or +1234567890"
              value={phone}
              onChange={handlePhoneChange}
              disabled={processing || mode === 'edit'}
              className={`pl-9 ${errors.phone ? 'border-destructive' : ''}`}
            />
          </div>
          {errors.phone && (
            <p className="text-xs text-destructive">{errors.phone}</p>
          )}
          {mode === 'edit' && (
            <div className="flex items-start gap-2 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Phone number cannot be changed after creation as it serves as the unique identifier in Onfleet.</span>
            </div>
          )}
          {mode === 'create' && (
            <p className="text-xs text-muted-foreground">
              Use +country code for international numbers (e.g., +44 for UK)
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Customer preferences, delivery instructions, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={processing}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">
            These are global notes for the recipient and will appear on all their tasks.
          </p>
        </div>
      </div>

      <Separator />

      {/* Notification Preferences Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MessageSquareOff className="h-4 w-4 text-muted-foreground" />
          Notification Preferences
        </div>

        <div className="space-y-3">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="skipSMSNotifications"
              checked={skipSMSNotifications}
              onCheckedChange={(checked) => setSkipSMSNotifications(checked === true)}
              disabled={processing}
            />
            <div className="grid gap-1.5 leading-none">
              <Label
                htmlFor="skipSMSNotifications"
                className="text-sm font-normal cursor-pointer"
              >
                Skip SMS notifications
              </Label>
              <p className="text-xs text-muted-foreground">
                Recipient will not receive task status updates via SMS
              </p>
            </div>
          </div>

          {mode === 'create' && (
            <div className="flex items-start space-x-2">
              <Checkbox
                id="skipPhoneNumberValidation"
                checked={skipPhoneNumberValidation}
                onCheckedChange={(checked) => setSkipPhoneNumberValidation(checked === true)}
                disabled={processing}
              />
              <div className="grid gap-1.5 leading-none">
                <Label
                  htmlFor="skipPhoneNumberValidation"
                  className="text-sm font-normal cursor-pointer"
                >
                  Skip phone validation
                </Label>
                <p className="text-xs text-muted-foreground">
                  Allow non-standard phone formats (requires + prefix for international)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex pt-2">
        <Button type="submit" disabled={processing} className="w-full">
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {mode === 'create' ? 'Creating Recipient...' : 'Updating Recipient...'}
            </>
          ) : (
            mode === 'create' ? 'Create Recipient' : 'Update Recipient'
          )}
        </Button>
      </div>
    </form>
  );
});

RecipientForm.displayName = 'RecipientForm';
